import { type JVSIcons, Label, type LabelProps } from '@mairistem/composants';
import { type Exercice, Sens } from '@mairistem/comptabilite';
import { Icon } from '@mairistem/finances-utils';
import Decimal from 'decimal.js';
import React from 'react';
import AUTORISATION_MODAL_ERROR from '../constants/autorisation/autorisationModalError';
import BLOCAGE_SAISIE_IMPUTATION, {
	type BlocageSaisieImputation,
} from '../constants/autorisation/blocageSaisieImputation';
import STATUT_PROJET from '../constants/common/statutProjet';
import type Financement from '../interfaces/common/financement';
import type ProImputation from '../interfaces/common/proImputation';
import type Projet from '../interfaces/common/projet';
import './autorisation.css';

export const renderLabel = (statut: number) => {
	let styles = {
		color: 'black',
		content: 'Inconnu',
		icon: 'CircleAlert',
	} as LabelProps;

	switch (statut) {
		case STATUT_PROJET.EN_COURS:
			styles = { color: 'blue', content: 'En cours', icon: 'Loader' };
			break;
		case STATUT_PROJET.BROUILLON:
			styles = { color: 'grey', content: 'Brouillon', icon: 'FilePen' };
			break;
		case STATUT_PROJET.SOLDE:
			styles = { color: 'green', content: 'Soldée', icon: 'Check' };
			break;
		default:
			break;
	}

	return (
		<Label
			basic
			circular
			className="autorisationStatusLabel"
			color={styles.color}
		>
			<Icon
				color={styles.color}
				iconSet="Lucide"
				name={styles.icon as JVSIcons}
				size={16}
			/>
			{styles.content}
		</Label>
	);
};

/**
 * Permet de savoir si l'exercice saisi est une annee complete
 */
export const isExerciceValide = (exercice: number) =>
	String(exercice).length === 4;

/**
 * Permet de savoir si une imputation (article ou chapitre) a ete saisie sur la ligne
 */
export const isProImputationSaisie = (proImputation: ProImputation) =>
	Boolean(proImputation.codeArticle || proImputation.codeChapitre);

/**
 * Permet de connaitre l'exercice comptable sur lequel saisir les imputations d'une annee :
 * l'exercice de l'annee s'il existe, sinon le dernier exercice si l'annee lui est posterieure
 */
export const getExerciceSaisie = (exercices: Exercice[], annee: number) => {
	const exercice = exercices.find((item) => item.anneeExercice === annee);

	if (exercice) return exercice;

	const dernierExercice = exercices.reduce<Exercice | null>(
		(acc, curr) =>
			!acc || curr.anneeExercice > acc.anneeExercice ? curr : acc,
		null,
	);

	return dernierExercice && annee > dernierExercice.anneeExercice
		? dernierExercice
		: null;
};

/**
 * Permet de savoir pourquoi les imputations ne peuvent pas etre saisies sur l'exercice de saisie
 * @returns La raison du blocage, ou null si la saisie est possible
 */
export const getBlocageSaisieImputation = (
	exerciceSaisie: Exercice | null,
	identifiantService: number | null,
	exercicesSansEspace: Set<number>,
): BlocageSaisieImputation | null => {
	if (!exerciceSaisie) return BLOCAGE_SAISIE_IMPUTATION.EXERCICE_INEXISTANT;

	if (identifiantService && exercicesSansEspace.has(exerciceSaisie.identifiant))
		return BLOCAGE_SAISIE_IMPUTATION.SERVICE_SANS_ESPACE;

	return null;
};

/**
 * Permet d'expliquer a l'utilisateur pourquoi les imputations ne peuvent pas etre saisies
 */
export const getLibelleBlocageSaisieImputation = (
	blocageSaisie: BlocageSaisieImputation,
	exerciceSaisie: Exercice | null,
) => {
	switch (blocageSaisie) {
		case BLOCAGE_SAISIE_IMPUTATION.EXERCICE_NON_SAISI:
			return 'Veuillez sélectionner un exercice';
		case BLOCAGE_SAISIE_IMPUTATION.EXERCICE_INEXISTANT:
			return "L'exercice n'existe pas en comptabilité";
		case BLOCAGE_SAISIE_IMPUTATION.SERVICE_SANS_ESPACE:
			return `Le service n'est rattaché à aucun espace budgétaire sur l'exercice ${exerciceSaisie?.anneeExercice}`;
		default:
			return null;
	}
};

export const validateStepInformations = (autorisation: Projet) => {
	const result = { error: null, validated: false };

	if (autorisation.code.length === 0) {
		result.error = AUTORISATION_MODAL_ERROR.CODE_REQUIRED;
		return result;
	}

	if (autorisation.libelle.length === 0) {
		result.error = AUTORISATION_MODAL_ERROR.LIBELLE_REQUIRED;
		return result;
	}

	if (!autorisation.identifiantEtiquette) {
		result.error = AUTORISATION_MODAL_ERROR.ETIQUETTE_REQUIRED;
		return result;
	}

	if (autorisation.montantAutorisation === 0) {
		result.error = AUTORISATION_MODAL_ERROR.MONTANT_REQUIRED;
		return result;
	}

	if (autorisation.composants.length > 0) {
		let montantTotalComposants = new Decimal(0);

		autorisation.composants.some((composant) => {
			if (composant.libelle.length === 0) {
				result.error = AUTORISATION_MODAL_ERROR.COMPOSANT_LIBELLE_REQUIRED;
				return true;
			}

			if (composant.montantEvenement === 0) {
				result.error = AUTORISATION_MODAL_ERROR.COMPOSANT_MONTANT_REQUIRED;
				return true;
			}

			montantTotalComposants = montantTotalComposants.add(
				composant.montantEvenement,
			);

			return false;
		});

		if (result.error) return result;

		if (montantTotalComposants.gt(autorisation.montantAutorisation)) {
			result.error = AUTORISATION_MODAL_ERROR.TOTAL_MONTANT_EVENEMENT_INCORRECT;
			return result;
		}
	}

	result.validated = true;
	return result;
};

export const validateStepCreditsPaiementRessources = (
	autorisation: Projet,
	hasEvenements: boolean,
	sens: string,
	exercices: Exercice[],
	exercicesSansEspace: Set<number>,
) => {
	const result = { error: null, validated: false };

	// Si l'imputation n'est pas saisie, on indique si c'est parce que la saisie est impossible
	const getProImputationRequiredError = (annee: number, libelle?: string) => {
		const exerciceSaisie = getExerciceSaisie(exercices, annee);

		switch (
			getBlocageSaisieImputation(
				exerciceSaisie,
				autorisation.identifiantService,
				exercicesSansEspace,
			)
		) {
			case BLOCAGE_SAISIE_IMPUTATION.EXERCICE_INEXISTANT:
				return AUTORISATION_MODAL_ERROR.PRO_IMPUTATION_EXERCICE_INEXISTANT(
					sens,
					annee,
					libelle,
				);
			case BLOCAGE_SAISIE_IMPUTATION.SERVICE_SANS_ESPACE:
				return AUTORISATION_MODAL_ERROR.PRO_IMPUTATION_SERVICE_SANS_ESPACE(
					sens,
					annee,
					exerciceSaisie.anneeExercice,
					libelle,
				);
			default:
				return AUTORISATION_MODAL_ERROR.PRO_IMPUTATION_IMPUTATION_REQUIRED(
					sens,
					annee,
					libelle,
				);
		}
	};

	const validateFinancements = (
		financements: Financement[],
		montantAutorisationOrComposant: number,
		libelle?: string,
	) => {
		let totalMontantsFinancement = new Decimal(0);

		// Check financement
		const hasErrorInFinancements = (financements ?? []).some((financement) => {
			if (financement.sens !== sens) return false;

			if (!financement.exercice) {
				result.error = AUTORISATION_MODAL_ERROR.FINANCEMENT_EXERCICE_REQUIRED(
					sens,
					libelle,
				);
				return true;
			}

			if (!isExerciceValide(financement.exercice)) {
				result.error = AUTORISATION_MODAL_ERROR.FINANCEMENT_EXERCICE_INCORRECT(
					sens,
					libelle,
				);
				return true;
			}

			if (financement.montant === 0) {
				result.error = AUTORISATION_MODAL_ERROR.FINANCEMENT_MONTANT_REQUIRED(
					sens,
					financement.exercice,
					libelle,
				);
				return true;
			}

			// Check proImputation
			let totalMontantsProImputation = new Decimal(0);

			const hasErrorInProImputations = financement.proImputations.some(
				(proImputation) => {
					if (!isProImputationSaisie(proImputation)) {
						result.error = getProImputationRequiredError(
							financement.exercice,
							libelle,
						);
						return true;
					}

					if (financement.sens === Sens.DEPENSE) {
						totalMontantsProImputation = totalMontantsProImputation
							.add(proImputation.montant)
							.add(proImputation.revision)
							.add(proImputation.rarN1)
							.sub(proImputation.rarN);
					}

					return false;
				},
			);

			// S'il y a des erreurs dans les proImputations, on sort de la boucle des financements
			if (hasErrorInProImputations) return true;

			const financementMontant = new Decimal(financement.montant)
				.add(financement.revision)
				.add(financement.rarN1)
				.sub(financement.rarN);

			if (totalMontantsProImputation.gt(financementMontant)) {
				result.error =
					AUTORISATION_MODAL_ERROR.TOTAL_MONTANT_PRO_IMPUTATION_INCORRECT(
						sens,
						financement.exercice,
						libelle,
					);
				return true;
			}

			totalMontantsFinancement =
				totalMontantsFinancement.add(financementMontant);

			return false;
		});

		// S'il y a des erreurs dans les financements, on sort de la boucle des composants
		if (hasErrorInFinancements) return true;

		if (totalMontantsFinancement.gt(montantAutorisationOrComposant)) {
			result.error =
				AUTORISATION_MODAL_ERROR.TOTAL_MONTANT_FINANCEMENT_INCORRECT(
					sens,
					libelle,
				);
			return true;
		}

		return false;
	};

	if (hasEvenements) {
		const hasErrorInComposants = autorisation.composants.some((composant) =>
			validateFinancements(
				composant.financements,
				composant.montantEvenement,
				composant.libelle,
			),
		);

		if (hasErrorInComposants) return result;
	} else {
		const hasErrorInFinancements = validateFinancements(
			autorisation.financements,
			autorisation.montantAutorisation,
		);

		if (hasErrorInFinancements) return result;
	}

	result.validated = true;
	return result;
};

/**
 * Permet de savoir si au moins une imputation a ete saisie sur l'autorisation (credits de paiement et ressources)
 */
export const hasProImputationsSaisies = (autorisation: Projet) =>
	[
		...(autorisation.financements ?? []),
		...(autorisation.composants ?? []).flatMap(
			(composant) => composant.financements ?? [],
		),
	].some((financement) =>
		financement.proImputations?.some(isProImputationSaisie),
	);
